import React, { createContext, useContext, useMemo } from 'react';
import { DEFAULT_LOCALE, DATE_LOCALES, localePath } from './config';
import en from './locales/en';
import tr from './locales/tr';
import es from './locales/es';
import fr from './locales/fr';
import de from './locales/de';
import it from './locales/it';
import nl from './locales/nl';

/**
 * UI strings for every locale ship in the main bundle. They are small and are
 * needed synchronously on first paint. Blog and legal copy is far bigger and
 * is loaded per locale on demand — see src/content/ContentContext.js.
 */
const STRINGS = { en, tr, es, fr, de, it, nl };

const LanguageContext = createContext(null);

function lookup(source, key) {
  return key.split('.').reduce((acc, part) => (acc == null ? undefined : acc[part]), source);
}

export function LanguageProvider({ locale, children }) {
  const value = useMemo(() => {
    const strings = STRINGS[locale] || STRINGS[DEFAULT_LOCALE];

    /**
     * Looks up a dot-separated key, falling back to English so a missing
     * translation renders readable copy instead of the raw key. `vars` fills
     * {placeholders}: t('blog.readTime', { n: 7 }) -> '7 min read'.
     */
    const t = (key, vars) => {
      const hit = lookup(strings, key);
      const value = hit !== undefined ? hit : lookup(STRINGS[DEFAULT_LOCALE], key);
      if (value === undefined) return key;
      if (!vars || typeof value !== 'string') return value;
      return value.replace(/\{(\w+)\}/g, (match, name) =>
        vars[name] !== undefined ? String(vars[name]) : match
      );
    };

    return {
      locale,
      t,
      strings,
      /** Builds an in-app href for the current locale: path('/blog') -> '/tr/blog'. */
      path: (p) => localePath(locale, p),
      formatDate: (iso) =>
        new Date(`${iso}T00:00:00`).toLocaleDateString(DATE_LOCALES[locale], {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
    };
  }, [locale]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useI18n must be used inside a LanguageProvider');
  return ctx;
}
